import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import { FormattedMessage } from 'react-intl';
import Hyperlink from '../common/Hyperlink';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import arrowForward from '../../assets/images/home/icon-arrow-forward.svg';

export const Section = styled.section`
  padding-left: var(--b-gutter);
  padding-right: var(--b-gutter);
  background-color: ${({ bg }) => bg || 'transparent'};
  color: ${({ color }) => color || 'inherit'};
`;

export const Inner = styled.div`
  width: 100%;
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  ${type('display', 'small', { emphasis: true })};
  margin: 0;
  padding: 0;
  color: inherit;
  text-wrap: balance;
`;

export const HeadlineRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  ${media.large`
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 32px;
  `};
`;

const ViewAll = styled(Hyperlink)`
  ${type('detail', 'medium', { uppercase: true })};
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
  color: inherit;
  text-decoration: none !important;
  white-space: nowrap;

  img {
    display: block;
    width: 30px;
    height: 30px;
    transition: transform 0.25s ease;
  }

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: underline !important;
    text-underline-offset: 3px;
  }

  &:hover img {
    transform: translateX(4px);
  }
`;

export const ViewAllLink = ({ to, openInNewTab }) => (
  <ViewAll to={to} openInNewTab={openInNewTab}>
    <img src={arrowForward} alt="" />
    <FormattedMessage id="home.view-all" defaultMessage="View all" />
  </ViewAll>
);

ViewAllLink.propTypes = {
  to: PropTypes.string.isRequired,
  openInNewTab: PropTypes.bool,
};

export const Cover = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
`;

export const imageHover = css`
  .image {
    overflow: hidden;
  }

  .image img {
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @media (hover: hover) {
    &:hover .image img {
      transform: scale(1.04);
    }
  }
`;

export const scrollRow = css`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin: 0 calc(var(--b-gutter) * -1);
  padding: 0 var(--b-gutter);
  scroll-padding: 0 var(--b-gutter);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  ${media.large`
    overflow: visible;
    margin: 0;
    padding: 0;
  `};
`;

const DotsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  button {
    position: relative;
    display: block;
    width: 8px;
    height: 8px;
    padding: 0;
    border: 0;
    border-radius: 100px;
    background-color: ${({ onDark }) => (onDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)')};
    cursor: pointer;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background-color: ${({ onDark }) => (onDark ? 'var(--b-white)' : 'var(--b-black)')};
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    &[aria-current='true']::after {
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }
`;

export const Dots = ({ count, active, onSelect, onDark, label, className }) => (
  <DotsWrapper onDark={onDark} className={className} role="tablist" aria-label={label}>
    {Array.from({ length: count }, (_, i) => (
      <button
        key={i}
        type="button"
        role="tab"
        aria-label={`${i + 1} / ${count}`}
        aria-current={i === active ? 'true' : undefined}
        aria-selected={i === active}
        onClick={() => onSelect(i)}
      />
    ))}
  </DotsWrapper>
);

Dots.propTypes = {
  count: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDark: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string,
};

Dots.defaultProps = {
  label: 'Slides',
};

export const slickFill = css`
  .slick-slider {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    user-select: none;
  }

  .slick-list {
    flex: 1 1 auto;
  }

  .slick-track {
    display: flex;
    height: 100%;
  }

  .slick-initialized .slick-slide {
    display: flex;
    height: auto;
    pointer-events: none;

    > div {
      display: flex;
      flex: 1;
    }
  }

  .slick-initialized .slick-slide.slick-active {
    z-index: 1;
    pointer-events: auto;
  }

  .slide {
    display: flex !important;
    flex: 1;
    flex-direction: column;
  }
`;

const SLIDER_SETTINGS = {
  arrows: false,
  dots: false,
  infinite: true,
  fade: true,
  speed: 600,
  cssEase: 'ease',
  slidesToShow: 1,
  slidesToScroll: 1,
};

export class SlideCarousel extends React.Component {
  state = { active: 0 };

  onBeforeChange = (current, next) => this.setState({ active: next });

  onSelect = index => {
    if (this.slider) this.slider.slickGoTo(index);
  };

  render() {
    const { children, renderDots, speed, easing } = this.props;
    return (
      <React.Fragment>
        <Slider
          ref={this.setSlider}
          {...SLIDER_SETTINGS}
          {...(speed ? { speed } : {})}
          {...(easing ? { cssEase: easing } : {})}
          beforeChange={this.onBeforeChange}
        >
          {children}
        </Slider>
        {renderDots(this.state.active, this.onSelect)}
      </React.Fragment>
    );
  }

  setSlider = slider => {
    this.slider = slider;
  };
}

SlideCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  renderDots: PropTypes.func.isRequired,
  speed: PropTypes.number,
  easing: PropTypes.string,
};



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/shared.js